import { approvalService } from '../../helpers/services/approval.service'
import i18n from '@/i18n'
import { sharedHelper, filtersHelper } from '@/utils'

export const state = {
  list: { data: [], pagination: { pageNumber: 0, pageSize: 0, lastPage: 0, tableRowsCount: 0 } },
  details: {},
  // How many documents are waiting, and whether the tenant is in manual mode
  // at all — the listing page says so rather than leaving an empty table
  // looking like a bug.
  pending: { count: 0, mode: 'auto' },
  status: { list: true, detail: true, save: true },
}

export const APPROVAL_MODULE = 'approval'

export const GET_ALL_APPROVALS = 'getAllApprovals'
export const GET_APPROVAL = 'getApproval'
export const APPROVE_APPROVAL = 'approveApproval'
export const REJECT_APPROVAL = 'rejectApproval'
export const DELETE_APPROVAL = 'deleteApproval'
export const GET_PENDING_COUNT = 'getPendingApprovalCount'
export const UPDATE_APPROVAL_PAYLOAD = 'updateApprovalPayload'

const REQUESTING = 'requesting'
const SUCCESS = 'commonSuccess'
const FAILURE = 'commonFailure'
const LIST_SUCCESS = 'listSuccess'
const DETAIL_SUCCESS = 'detailSuccess'
const PENDING_SUCCESS = 'pendingSuccess'

/**
 * A hold raised because approval mode is manual reads differently from one the
 * credit limit caught: the first is routine, the second is a warning. The row
 * carries both so the listing can say which without re-reading the remarks.
 */
function mapRow(item) {
  return {
    id: item.uuid,
    docType: item.doc_type,
    action: item.action || 'create',
    sourceDocNo: item.source_doc_no || null,
    triggerReason: item.trigger_reason || null,
    docNo: item.doc_no,
    debtorCode: item.debtor_code,
    debtorName: item.debtor_name,
    amount: item.amount,
    status: item.status,
    remarks: item.remarks,
    requestedByName: item.requested_by_user?.name || item.requested_by,
    requestedAt: filtersHelper.getDateFormat(item.requested_at),
    approvedByName: item.approved_by_user?.name || item.approved_by,
    approvedAt: filtersHelper.getDateFormat(item.approved_at),
    rejectionReason: item.rejection_reason,
    payload: item.payload || null,
    originalPayload: item.original_payload || null,
    // Server-computed: null for a create, and for an edit whose snapshot of the
    // live document could not be read.
    changes: item.changes || null,
  }
}

export const actions = {
  [GET_ALL_APPROVALS]({ commit }, { filter }) {
    commit(REQUESTING, 'list')
    approvalService.getAllApprovals(filter).then((r) => {
      commit(LIST_SUCCESS, r); commit(SUCCESS)
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_APPROVAL]({ commit }, { id }) {
    commit(REQUESTING, 'detail')
    return approvalService.getApproval(id).then((r) => {
      commit(DETAIL_SUCCESS, r); commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [APPROVE_APPROVAL]({ commit, dispatch }, { id, data, filter }) {
    commit(REQUESTING, 'save')
    return approvalService.approveApproval(id, data).then((r) => {
      sharedHelper.alertSuccessMessage(i18n.t('label.approve'))
      commit(SUCCESS); if (filter) dispatch(GET_ALL_APPROVALS, { filter }); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [UPDATE_APPROVAL_PAYLOAD]({ commit }, { id, data }) {
    commit(REQUESTING, 'save')
    return approvalService.updateApprovalPayload(id, data).then((r) => {
      sharedHelper.alertSuccessMessage(i18n.t('label.documentUpdated'))
      commit(DETAIL_SUCCESS, r); commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_PENDING_COUNT]({ commit }) {
    return approvalService.getPendingCount().then((r) => {
      commit(PENDING_SUCCESS, r); return r
    }).catch(() => {
      // A missing badge is not worth an error toast.
    })
  },
  /**
   * Quiet on success, like deleting a document: the row leaving the list is
   * the confirmation. The caller refreshes whichever list it is showing.
   */
  [DELETE_APPROVAL]({ commit }, { id }) {
    commit(REQUESTING, 'save')
    return approvalService.deleteApproval(id).then((r) => {
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [REJECT_APPROVAL]({ commit, dispatch }, { id, data, filter }) {
    commit(REQUESTING, 'save')
    return approvalService.rejectApproval(id, data).then((r) => {
      sharedHelper.alertSuccessMessage('Rejected')
      commit(SUCCESS); if (filter) dispatch(GET_ALL_APPROVALS, { filter }); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
}

export const mutations = {
  [LIST_SUCCESS](state, data) {
    const items = data.data || []
    state.list.pagination = sharedHelper.apiPaginationAdapter(data.pagination)
    state.list.data = data.success ? items.map(mapRow) : items
  },
  [DETAIL_SUCCESS](state, data) {
    state.details = data.data ? mapRow(data.data) : {}
  },
  [PENDING_SUCCESS](state, data) {
    state.pending = { count: data?.data?.pending || 0, mode: data?.data?.mode || 'auto' }
  },
  [FAILURE](state) { state.status = { list: true, detail: true, save: true } },
  [REQUESTING](state, type) { state.status[type] = false },
  [SUCCESS](state) { state.status = { list: true, detail: true, save: true } },
}
